import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import storage from "./storage";
import api from "./api";
import { resolveIdentity } from "./sync";

const ProContext = createContext({ isPro: false, refreshPro: async () => {} });

export function ProProvider({ children }) {
  const [isPro, setIsProState] = useState(() => storage.getIsPro());

  const setIsPro = useCallback((val) => {
    const v = !!val;
    storage.setIsPro(v);
    setIsProState(v);
    window.dispatchEvent(new CustomEvent("sm:pro-refreshed", { detail: { isPro: v } }));
  }, []);

  const refreshPro = useCallback(async () => {
    try {
      // resolveIdentity() dedupes /api/auth/me across the app and settles
      // to the authed uid when a cookie session is valid, else the anon
      // uuid. Every subsequent user-scoped fetch on this session uses the
      // same id.
      const uid = await resolveIdentity();
      // Cached fetch (60s TTL) — RouteProRefresher fires this on every nav,
      // so without caching every route change hammers /api/users/:id.
      const user = await api.getUserCached(uid);
      // Server response is authoritative. The backend already folds grace
      // periods into `is_pro`; a raw subscription id must not unlock Pro
      // before the first successful charge.
      const pro = !!user?.is_pro;
      setIsPro(pro);
      return pro;
    } catch (_) {
      // Keep cached value on network error
      return storage.getIsPro();
    }
  }, [setIsPro]);

  useEffect(() => {
    // Always verify from server on mount
    refreshPro();
    // Refresh when returning to foreground (post-PayPal redirect on mobile)
    const onFocus = () => refreshPro();
    window.addEventListener("focus", onFocus);
    // Honour external broadcasts (e.g. from ProSuccess page, webhook poll)
    const onBroadcast = (e) => {
      const next = !!e?.detail?.isPro;
      storage.setIsPro(next);
      setIsProState(next);
    };
    window.addEventListener("sm:pro-refreshed", onBroadcast);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("sm:pro-refreshed", onBroadcast);
    };
  }, [refreshPro]);

  return (
    <ProContext.Provider value={{ isPro, refreshPro, setIsPro }}>
      {children}
    </ProContext.Provider>
  );
}

export const useIsPro = () => useContext(ProContext).isPro;
export const useProContext = () => useContext(ProContext);
